import { motion } from "framer-motion";
import { MapPin, Phone, ArrowRight, Zap } from "lucide-react";

/**
 * Temporäre Vorschau-Seite: zeigt 3 Anime/Manga Design-Richtungen
 * für den Hero-Bereich + eine Beispiel-Karte. Route: /preview
 */

const VariantLabel = ({ n, name, sub }) => (
  <div
    data-testid={`variant-label-${n}`}
    className="sticky top-0 z-50 flex items-center gap-4 border-b border-white/10 bg-black px-6 py-4"
  >
    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-lg font-black text-black">
      {n}
    </span>
    <div>
      <p className="text-sm font-bold uppercase tracking-widest text-white">{name}</p>
      <p className="text-xs text-white/50">{sub}</p>
    </div>
  </div>
);

/* ============================================================= */
/* VARIANTE 1 — Raw Manga Ink & Screentone                       */
/* ============================================================= */
const VariantA = () => (
  <section
    data-testid="preview-variant-a"
    className="relative overflow-hidden bg-[hsl(0,0%,98%)] px-6 py-16 text-[hsl(0,0%,5%)]"
    style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
  >
    {/* Screentone halftone */}
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 opacity-[0.5]"
      style={{
        backgroundImage: "radial-gradient(hsl(0,0%,5%) 1.1px, transparent 1.1px)",
        backgroundSize: "10px 10px",
        WebkitMaskImage: "linear-gradient(115deg, black 0%, transparent 55%)",
        maskImage: "linear-gradient(115deg, black 0%, transparent 55%)",
      }}
    />
    {/* Red speedlines */}
    <div
      aria-hidden
      className="pointer-events-none absolute -right-40 top-1/2 h-[120%] w-[60%] -translate-y-1/2 opacity-30"
      style={{
        background:
          "repeating-conic-gradient(from 0deg at 80% 50%, hsl(350,90%,55%) 0deg 0.7deg, transparent 0.7deg 6deg)",
      }}
    />
    <div className="relative mx-auto grid max-w-6xl items-center gap-10 md:grid-cols-2">
      <div>
        <span
          className="inline-block rotate-[-3deg] border-4 border-black bg-[hsl(350,90%,55%)] px-4 py-1 text-sm font-bold uppercase tracking-wider text-white"
          style={{ boxShadow: "5px 5px 0 #000" }}
        >
          Köln · Zülpicherstr. 357
        </span>
        <h1
          className="mt-6 leading-[0.85]"
          style={{ fontFamily: "'Anton', sans-serif", fontSize: "clamp(3rem,7vw,6rem)" }}
        >
          DEIN ANIME-
          <br />& TCG-SPOT
          <br />
          <span
            className="text-[hsl(350,90%,55%)]"
            style={{ WebkitTextStroke: "2px #000" }}
          >
            IN KÖLN
          </span>
        </h1>
        <p className="mt-5 max-w-md text-base font-medium leading-relaxed">
          Pokémon, One Piece &amp; viele weitere TCGs, detailreiche Figuren und
          eine Community die tauscht, spielt und fachsimpelt.
        </p>
        <div className="mt-7 flex flex-wrap gap-4">
          <button
            className="border-4 border-black bg-black px-7 py-3 font-bold uppercase tracking-wide text-white transition-transform hover:translate-x-[3px] hover:translate-y-[3px]"
            style={{ boxShadow: "6px 6px 0 hsl(350,90%,55%)" }}
          >
            Jetzt anrufen
          </button>
          <button
            className="border-4 border-black bg-white px-7 py-3 font-bold uppercase tracking-wide text-black transition-transform hover:translate-x-[3px] hover:translate-y-[3px]"
            style={{ boxShadow: "6px 6px 0 #000" }}
          >
            WhatsApp
          </button>
        </div>
      </div>
      <div className="relative">
        {/* Manga-Panel "Bild" */}
        <div
          className="relative aspect-[4/5] overflow-hidden border-4 border-black bg-[hsl(0,0%,90%)]"
          style={{ boxShadow: "10px 10px 0 #000" }}
        >
          <img
            src="https://images.pexels.com/photos/7783409/pexels-photo-7783409.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt=""
            className="h-full w-full object-cover"
            style={{ filter: "grayscale(1) contrast(1.4)" }}
          />
          <span
            className="absolute right-3 top-3 font-black text-black"
            style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "2rem" }}
          >
            渋谷
          </span>
        </div>
        {/* SFX */}
        <span
          className="absolute -left-6 bottom-6 rotate-[-8deg] text-[hsl(350,90%,55%)]"
          style={{
            fontFamily: "'Bangers', cursive",
            fontSize: "4rem",
            WebkitTextStroke: "3px #000",
          }}
        >
          ドン!
        </span>
      </div>
    </div>
  </section>
);

/* ============================================================= */
/* VARIANTE 2 — Neo-Tokyo Cyberpunk Nights                       */
/* ============================================================= */
const VariantB = () => (
  <section
    data-testid="preview-variant-b"
    className="relative flex min-h-[80vh] items-center overflow-hidden px-6 py-16"
    style={{ background: "hsl(240,60%,4%)", fontFamily: "'Rajdhani', sans-serif" }}
  >
    <img
      src="https://images.unsplash.com/photo-1519608487953-e999c86e7455?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80"
      alt=""
      className="absolute inset-0 h-full w-full object-cover opacity-40"
    />
    <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, hsl(240,60%,4%) 10%, hsl(240,60%,4%,0.4) 60%, transparent)" }} />
    {/* Scanlines */}
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 opacity-[0.25]"
      style={{
        backgroundImage:
          "repeating-linear-gradient(0deg, rgba(255,255,255,0.08) 0px, rgba(255,255,255,0.08) 1px, transparent 1px, transparent 3px)",
      }}
    />
    <div className="relative mx-auto w-full max-w-6xl">
      <span
        className="inline-block rounded-sm border border-[hsl(180,100%,50%)] px-3 py-1 text-xs uppercase tracking-[0.3em] text-[hsl(180,100%,50%)]"
        style={{ fontFamily: "'Space Mono', monospace", boxShadow: "0 0 16px hsl(180,100%,50%,0.5)" }}
      >
        ◤ Köln · Zülpicherstr. 357 ◢
      </span>
      <h1
        className="glitch mt-6 font-bold uppercase leading-none text-white"
        style={{ fontFamily: "'Chakra Petch', sans-serif", fontSize: "clamp(3rem,7vw,6rem)" }}
      >
        Dein Anime- &amp;
        <br />
        <span
          style={{
            color: "hsl(180,100%,50%)",
            textShadow:
              "0 0 12px hsl(180,100%,50%), 3px 0 hsl(320,100%,50%), -3px 0 hsl(180,100%,50%)",
          }}
        >
          TCG-Spot
        </span>{" "}
        in Köln
      </h1>
      <p className="mt-5 max-w-lg text-lg text-white/70">
        Tokyo-Style trifft Sammlerherz. Neon-Nächte, seltene Karten und eine
        Community die niemals schläft.
      </p>
      <div className="mt-8 flex flex-wrap gap-4">
        <button
          className="rounded-sm bg-[hsl(180,100%,50%)] px-7 py-3 font-bold uppercase tracking-wide text-black transition-shadow"
          style={{ boxShadow: "0 0 24px hsl(180,100%,50%,0.7)" }}
        >
          Jetzt anrufen
        </button>
        <button
          className="rounded-sm border border-[hsl(320,100%,50%)] bg-[hsl(320,100%,50%,0.08)] px-7 py-3 font-bold uppercase tracking-wide text-[hsl(320,100%,60%)] backdrop-blur"
          style={{ boxShadow: "0 0 24px hsl(320,100%,50%,0.4)" }}
        >
          WhatsApp
        </button>
      </div>
      {/* Glass card */}
      <div
        className="mt-10 inline-flex gap-8 rounded-xl border border-[hsl(180,100%,50%,0.3)] p-5 backdrop-blur-xl"
        style={{ background: "hsl(240,20%,8%,0.5)" }}
      >
        {[["TCG", "Riesige Auswahl"], ["100%", "Vor Ort"], ["€", "Sofort-Ankauf"]].map((s) => (
          <div key={s[1]}>
            <p className="text-2xl font-bold text-[hsl(180,100%,50%)]">{s[0]}</p>
            <p className="text-xs uppercase tracking-wider text-white/50" style={{ fontFamily: "'Space Mono', monospace" }}>{s[1]}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ============================================================= */
/* VARIANTE 3 — Shonen Action Energy (TCG Holographic)           */
/* ============================================================= */
const VariantC = () => (
  <section
    data-testid="preview-variant-c"
    className="relative flex min-h-[80vh] items-center overflow-hidden px-6 py-16"
    style={{ background: "hsl(220,10%,8%)", fontFamily: "'Outfit', sans-serif" }}
  >
    {/* Burst speedlines */}
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0"
      style={{
        background:
          "repeating-conic-gradient(from 0deg at 35% 45%, hsl(30,100%,55%,0.10) 0deg 0.6deg, transparent 0.6deg 5deg)",
      }}
    />
    <div className="relative mx-auto grid w-full max-w-6xl items-center gap-10 md:grid-cols-2">
      <div>
        <span
          className="inline-block bg-[hsl(30,100%,55%)] px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-black"
          style={{ clipPath: "polygon(0 0, 100% 0, 92% 100%, 0% 100%)", fontFamily: "'Oswald', sans-serif" }}
        >
          Köln · Zülpicherstr. 357
        </span>
        <h1
          className="mt-5 uppercase leading-[0.9] text-white"
          style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(3.5rem,8vw,7rem)", letterSpacing: "0.02em" }}
        >
          Dein Anime- &amp;
          <br />
          TCG-Spot{" "}
          <span style={{ color: "hsl(30,100%,55%)", textShadow: "0 0 24px hsl(30,100%,55%,0.7)" }}>
            in Köln
          </span>
        </h1>
        <p className="mt-5 max-w-md text-base text-white/65">
          Volle Energie für Sammler: hol dir die seltensten Karten, smashe ins
          nächste Set-Release und werde Teil der Crew.
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          <button
            className="bg-[hsl(30,100%,55%)] px-8 py-3.5 font-bold uppercase tracking-wide text-black"
            style={{ clipPath: "polygon(0 0, 100% 0, 94% 100%, 0% 100%)", fontFamily: "'Oswald', sans-serif", boxShadow: "0 12px 30px hsl(30,100%,50%,0.4)" }}
          >
            <Zap className="mr-1 inline h-4 w-4" /> Jetzt anrufen
          </button>
          <button
            className="border-2 border-[hsl(210,100%,60%)] px-8 py-3.5 font-bold uppercase tracking-wide text-[hsl(210,100%,70%)]"
            style={{ clipPath: "polygon(6% 0, 100% 0, 100% 100%, 0% 100%)", fontFamily: "'Oswald', sans-serif" }}
          >
            WhatsApp
          </button>
        </div>
      </div>
      {/* Holographic TCG card */}
      <motion.div
        className="relative mx-auto"
        initial={{ rotate: -6, y: 20, opacity: 0 }}
        animate={{ rotate: -6, y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 120, damping: 12 }}
        whileHover={{ rotate: 0, scale: 1.04 }}
        style={{ perspective: 1000 }}
      >
        <div className="relative aspect-[3/4] w-64 overflow-hidden rounded-2xl border-4 border-[hsl(30,100%,55%)]">
          <img
            src="https://images.pexels.com/photos/7809125/pexels-photo-7809125.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt=""
            className="h-full w-full object-cover"
          />
          {/* Holo foil */}
          <div
            className="absolute inset-0 mix-blend-color-dodge"
            style={{
              background:
                "linear-gradient(115deg, transparent 20%, hsl(30,100%,60%,0.5) 40%, hsl(210,100%,60%,0.5) 50%, hsl(300,100%,60%,0.5) 60%, transparent 80%)",
            }}
          />
          <span className="absolute bottom-3 right-3 font-black text-white" style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "1.5rem", textShadow: "0 0 10px hsl(30,100%,55%)" }}>
            渋谷
          </span>
        </div>
      </motion.div>
    </div>
  </section>
);

export default function StylePreview() {
  return (
    <div data-testid="style-preview-page" className="min-h-screen bg-black">
      <div className="bg-black px-6 py-8 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-white/40">Shibuya Store · Design-Vorschau</p>
        <h1 className="mt-2 text-2xl font-bold text-white">Wähle deinen Anime/Manga-Look</h1>
        <p className="mx-auto mt-2 max-w-xl text-sm text-white/50">
          Scrolle durch die 3 Varianten. Sag mir die Nummer die dir am besten
          gefällt – dann setze ich sie auf der ganzen Seite um.
        </p>
      </div>

      <VariantLabel n={1} name="Raw Manga Ink & Screentone" sub="Schwarz-weiß Manga-Panels · Halftone · Rot-Akzent · neo-brutalistisch" />
      <VariantA />

      <VariantLabel n={2} name="Neo-Tokyo Cyberpunk Nights" sub="Neon Cyan/Magenta · Glasmorphismus · Scanlines · Glitch" />
      <VariantB />

      <VariantLabel n={3} name="Shonen Action Energy" sub="Orange/Blau · Holografische TCG-Karte · Speedlines · 3D-Tilt" />
      <VariantC />

      <div className="px-6 py-12 text-center">
        <p className="text-sm text-white/60">
          Welche Variante soll ich umsetzen? Antworte einfach mit <span className="font-bold text-white">1</span>, <span className="font-bold text-white">2</span> oder <span className="font-bold text-white">3</span>.
        </p>
      </div>
    </div>
  );
}
